import { For, Show } from "solid-js";
import type { BriefEditSection } from "../../enums/brief";
import { BRIEF_EDIT_SECTION, BRIEF_STATUS } from "../../enums/brief";
import { isQuestionResolved } from "./briefReadiness";
import type { BriefQuestion, DeliveryBrief } from "./briefTypes";

export function BriefDocument(props: {
  brief: DeliveryBrief;
  onEdit: (section: BriefEditSection, trigger: HTMLElement) => void;
  onRecordResponse: (id: string, trigger: HTMLElement) => void;
  onReopenQuestion: (id: string) => void;
}) {
  return (
    <article class="brief-document" aria-labelledby="brief-document-title">
      <header class="brief-document-head">
        <div>
          <div class="brief-document-meta">
            <span class={`brief-source-badge is-${props.brief.sourceType}`}>
              {props.brief.source.label}
            </span>
            <span class={`brief-status is-${statusClass(props.brief.status)}`}>
              {props.brief.status}
            </span>
          </div>
          <h2 id="brief-document-title">{props.brief.title}</h2>
          <p>
            {props.brief.owner === "Not assigned"
              ? "Owner not assigned"
              : `Owner: ${props.brief.owner}`}
            {" / "}
            {props.brief.pages.length > 0
              ? `${props.brief.pages.length} affected ${props.brief.pages.length === 1 ? "page" : "pages"}`
              : "Origin resource scope"}
          </p>
        </div>
        <button
          type="button"
          class="brief-edit-action"
          onClick={(event) =>
            props.onEdit(BRIEF_EDIT_SECTION.IDENTITY, event.currentTarget)
          }
        >
          Edit draft
        </button>
      </header>

      <div class="brief-document-body">
        <DocumentSection
          title="Objective"
          section={BRIEF_EDIT_SECTION.OBJECTIVE}
          onEdit={props.onEdit}
        >
          <p class="brief-document-lede">{props.brief.objective}</p>
        </DocumentSection>

        <DocumentSection title="Current problem">
          <p>{props.brief.currentProblem}</p>
        </DocumentSection>

        <DocumentSection
          title="Required change"
          section={BRIEF_EDIT_SECTION.REQUIRED_CHANGE}
          onEdit={props.onEdit}
        >
          <p class="brief-required-change">{props.brief.requiredChange}</p>
        </DocumentSection>

        <DocumentSection
          title="Scope"
          section={BRIEF_EDIT_SECTION.SCOPE}
          onEdit={props.onEdit}
        >
          <p>{props.brief.scopeNotes}</p>
          <ul class="brief-scope-list">
            <For each={props.brief.pages}>
              {(page) => (
                <li>
                  <Show
                    when={page.detailRoute}
                    fallback={<strong>{page.title}</strong>}
                  >
                    {(route) => <a href={route()}>{page.title}</a>}
                  </Show>
                  <small>{page.path}</small>
                </li>
              )}
            </For>
          </ul>
        </DocumentSection>

        <Show when={props.brief.outOfScopeNotes}>
          <DocumentSection
            title="Out of scope"
            section={BRIEF_EDIT_SECTION.SCOPE}
            onEdit={props.onEdit}
          >
            <p>{props.brief.outOfScopeNotes}</p>
          </DocumentSection>
        </Show>

        <Show
          when={props.brief.requirements.some(
            (block) => block.items.length > 0,
          )}
        >
          <DocumentSection
            title="Relevant requirements"
            section={BRIEF_EDIT_SECTION.REQUIREMENTS}
            onEdit={props.onEdit}
          >
            <div class="brief-requirement-blocks">
              <For
                each={props.brief.requirements.filter(
                  (block) => block.items.length > 0,
                )}
              >
                {(block) => (
                  <section>
                    <h4>{block.title}</h4>
                    <ul>
                      <For each={block.items}>{(item) => <li>{item}</li>}</For>
                    </ul>
                  </section>
                )}
              </For>
            </div>
          </DocumentSection>
        </Show>

        <DocumentSection
          title="Acceptance criteria"
          section={BRIEF_EDIT_SECTION.ACCEPTANCE}
          onEdit={props.onEdit}
        >
          <ol class="brief-criteria-list">
            <For each={props.brief.acceptanceCriteria}>
              {(criterion) => <li>{criterion}</li>}
            </For>
          </ol>
        </DocumentSection>

        <Show when={props.brief.openQuestions.length > 0}>
          <DocumentSection
            title="Open questions"
            section={BRIEF_EDIT_SECTION.QUESTIONS}
            onEdit={props.onEdit}
          >
            <QuestionList
              questions={props.brief.openQuestions}
              onRecordResponse={props.onRecordResponse}
              onReopenQuestion={props.onReopenQuestion}
            />
          </DocumentSection>
        </Show>
      </div>
    </article>
  );
}

function DocumentSection(props: {
  title: string;
  section?: BriefEditSection;
  onEdit?: (section: BriefEditSection, trigger: HTMLElement) => void;
  children: import("solid-js").JSX.Element;
}) {
  return (
    <section class="brief-document-section">
      <header>
        <h3>{props.title}</h3>
        <Show when={props.section && props.onEdit}>
          <button
            type="button"
            onClick={(event) =>
              props.onEdit?.(
                props.section as BriefEditSection,
                event.currentTarget,
              )
            }
          >
            Edit
          </button>
        </Show>
      </header>
      {props.children}
    </section>
  );
}

function QuestionList(props: {
  questions: BriefQuestion[];
  onRecordResponse: (id: string, trigger: HTMLElement) => void;
  onReopenQuestion: (id: string) => void;
}) {
  return (
    <ul class="brief-question-list">
      <For each={props.questions}>
        {(question) => {
          const resolved = () => isQuestionResolved(question);
          return (
            <li
              id={`brief-question-${question.id}`}
              tabindex="-1"
              class={resolved() ? "is-resolved" : "is-open"}
            >
              <span>{resolved() ? "Recorded" : "Needs input"}</span>
              <div>
                <strong>{question.text}</strong>
                <Show when={resolved()}>
                  <p>{question.response}</p>
                  <Show when={question.confirmationSource.trim()}>
                    <small>
                      Response source: {question.confirmationSource}
                    </small>
                  </Show>
                </Show>
                <div class="brief-question-actions">
                  <button
                    type="button"
                    onClick={(event) =>
                      props.onRecordResponse(question.id, event.currentTarget)
                    }
                  >
                    {resolved() ? "Edit response" : "Record response"}
                  </button>
                  <Show when={resolved()}>
                    <button
                      type="button"
                      onClick={() => props.onReopenQuestion(question.id)}
                    >
                      Reopen
                    </button>
                  </Show>
                </div>
              </div>
            </li>
          );
        }}
      </For>
    </ul>
  );
}

function statusClass(status: DeliveryBrief["status"]): string {
  if (status === BRIEF_STATUS.READY_FOR_DELIVERY) return "ready";
  if (status === BRIEF_STATUS.NEEDS_INPUT) return "input";
  return "draft";
}
